import React from 'react'
import { useUserAuthSessionStore } from '@/store'
import { IS_SHOW_AUTH_DEBUG } from '@/config/app'

/**
 * Отладочная панель для мониторинга состояния аутентификации
 * Показывает текущие токены и позволяет тестировать refresh
 */
export const AuthDebugPanel: React.FC = () => {
  const {
    token,
    refreshToken,
    isSessionOpen,
    initializeSession,
    removeSession,
  } = useUserAuthSessionStore()

  const handleTestRefresh = async () => {
    if (refreshToken) {
      try {
        // Импортируем API для прямого вызова refresh
        const { api } = await import('@/api/api')

        // Делаем реальный запрос на refresh токенов
        const response = await api.post('/v1/auth/refresh-tokens', {
          refresh_token: refreshToken,
        })

        const { access_token, refresh_token: newRefreshToken } = response.data

        // Сохраняем новые токены
        await initializeSession({
          token: access_token,
          refreshToken: newRefreshToken,
        })
      } catch (error) {
        // Refresh failed
      }
    }
  }

  const handleClearSession = async () => {
    try {
      await removeSession()
    } catch (error) {
      // Failed to clear session
    }
  }

  const handleCheckLocalStorage = () => {
    const token = localStorage.getItem('auth_token')
    const refreshToken = localStorage.getItem('auth_refresh_token')
    const sessionOpen = localStorage.getItem('session_open')

    alert(`LocalStorage check completed.`)
  }

  const handleTest401 = async () => {
    try {
      // Импортируем API для прямого вызова
      const { api } = await import('@/api/api')

      // Делаем запрос с заведомо неверным токеном, чтобы получить 401
      const originalAuth = api.defaults.headers.common['Authorization']
      api.defaults.headers.common['Authorization'] =
        'Bearer invalid_token_to_trigger_401'

      try {
        // Любой защищенный эндпоинт, который вернет 401
        await api.get('/v1/identity/me')
      } catch (error) {
        // Восстанавливаем оригинальный заголовок
        if (originalAuth) {
          api.defaults.headers.common['Authorization'] = originalAuth
        } else {
          delete api.defaults.headers.common['Authorization']
        }
      }
    } catch (error) {
      // Failed to test 401
    }
  }

  // Показываем панель только если включена в конфиге
  if (!IS_SHOW_AUTH_DEBUG) {
    return null
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: '10px',
        right: '10px',
        background: '#f0f0f0',
        border: '1px solid #ccc',
        borderRadius: '8px',
        padding: '16px',
        fontSize: '12px',
        fontFamily: 'monospace',
        zIndex: 9999,
        maxWidth: '300px',
      }}
    >
      <h4 style={{ margin: '0 0 12px 0', color: '#333' }}>
        🔐 Auth Debug Panel
      </h4>

      <div style={{ marginBottom: '8px' }}>
        <strong>Session Open:</strong> {isSessionOpen ? '✅' : '❌'}
      </div>

      <div style={{ marginBottom: '8px' }}>
        <strong>Access Token:</strong>{' '}
        {token ? `${token.substring(0, 20)}...` : 'None'}
      </div>

      <div style={{ marginBottom: '12px' }}>
        <strong>Refresh Token:</strong>{' '}
        {refreshToken ? `${refreshToken.substring(0, 20)}...` : 'None'}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <button
          onClick={handleTestRefresh}
          disabled={!refreshToken}
          style={{
            padding: '4px 8px',
            fontSize: '11px',
            cursor: refreshToken ? 'pointer' : 'not-allowed',
            opacity: refreshToken ? 1 : 0.5,
            backgroundColor: '#51cf66',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
          }}
        >
          🔄 Test Direct Refresh
        </button>

        <button
          onClick={handleTest401}
          disabled={!refreshToken}
          style={{
            padding: '4px 8px',
            fontSize: '11px',
            cursor: refreshToken ? 'pointer' : 'not-allowed',
            opacity: refreshToken ? 1 : 0.5,
            backgroundColor: '#ffd43b',
            color: '#333',
            border: 'none',
            borderRadius: '4px',
          }}
        >
          ⚡ Test 401 Interceptor
        </button>

        <button
          onClick={handleClearSession}
          style={{
            padding: '4px 8px',
            fontSize: '11px',
            cursor: 'pointer',
            backgroundColor: '#ff6b6b',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
          }}
        >
          🗑️ Clear Session
        </button>

        <button
          onClick={handleCheckLocalStorage}
          style={{
            padding: '4px 8px',
            fontSize: '11px',
            cursor: 'pointer',
            backgroundColor: '#4ecdc4',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
          }}
        >
          🔍 Check Storage
        </button>
      </div>
    </div>
  )
}
